import { useState } from "react";
import { toast } from "react-toastify";
import { FiCheck } from "react-icons/fi";
import { getMyResults, acknowledgeResult } from "../../services/resultApi";
import { useCycle } from "../../context/CycleContext";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Loader, ErrorState, EmptyState, StatusBadge, Tabs, Banner, Button } from "../../components/common";
import { getErrorMessage } from "../../utils/errors";
import ScoreBreakdown from "../../components/performance/ScoreBreakdown";
import RatingScale from "../../components/performance/RatingScale";
import GoalList from "../../components/goals/GoalList";
import { formatDate } from "../../utils/format";
import { RESULT_STATUS } from "../../utils/constants";

const api = { getMyResults, acknowledgeResult };

export default function MyResultsPage() {
  const { cycleId } = useCycle();
  const { data, error, reload } = useAsync(() => api.getMyResults(), []);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;
  if (!data.length) {
    return (
      <div className="page">
        <PageHeader title="My results" />
        <Card><EmptyState title="No results yet" text="Your finalized appraisals will appear here." /></Card>
      </div>
    );
  }

  const current = data.find((r) => r.cycle.id === selected) || data.find((r) => r.cycle.id === String(cycleId)) || data.find((r) => !r.draft) || data[0];
  const finalized = current.status === RESULT_STATUS.FINALIZED;
  const rating = { data: current.config };

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.acknowledgeResult(current.revisionId);
      toast.success("Thanks, your acknowledgment is recorded.");
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader title="My results" subtitle="Your final ratings appear once HR finalizes each cycle." />
      <Tabs tabs={data.map((r) => ({ key: r.cycle.id, label: r.cycle.name }))} value={current.cycle.id} onChange={setSelected} />

      {!finalized ? (
        <Card>
          <EmptyState
            title="Not yet rated"
            text={`${current.cycle.name} is at the "${current.status}" step. Your score and rating will show here after HR finalizes the appraisal.`}
            action={<StatusBadge status={current.status} />}
          />
        </Card>
      ) : (
        <>
          <Banner
            tone="success"
            title={`Released on ${formatDate(current.releasedAt || current.finalizedAt)}`}
            action={!current.acknowledged && current.revisionId && <Button size="sm" icon={FiCheck} loading={busy} onClick={acknowledge}>Acknowledge result</Button>}
          >
            {current.acknowledged
              ? `You acknowledged this result on ${formatDate(current.acknowledgedAt)}. Contact HR if you think something needs correcting.`
              : current.acknowledgmentRequired
                ? "Please acknowledge that you have seen this result. Acknowledging does not mean you agree with it."
                : "This result is locked. Contact HR if you think something needs correcting."}
          </Banner>
          <Card>
            <ScoreBreakdown performance={current.performance} weights={rating.data?.weights} restricted={rating.data?.restricted} scoreCap={rating.data?.scoreCap} title={`${current.cycle.name} final score`} />
          </Card>
          {!rating.data?.restricted && (
            <Card title="Where your score sits">
              <RatingScale score={current.performance.finalScore} bands={rating.data?.bands} />
            </Card>
          )}
          {current.managerFeedback && (
            <Card title={`Feedback from ${current.managerFeedback.managerName}`}>
              <div className="feedback-grid">
                <div><p className="detail-label">Overall</p><p className="detail-text">{current.managerFeedback.comments || "—"}</p></div>
                <div><p className="detail-label">Strengths</p><p className="detail-text">{current.managerFeedback.strengths || "—"}</p></div>
                <div><p className="detail-label">Areas to improve</p><p className="detail-text">{current.managerFeedback.improvementAreas || "—"}</p></div>
                <div><p className="detail-label">Development plan</p><p className="detail-text">{current.managerFeedback.developmentPlan || "—"}</p></div>
              </div>
            </Card>
          )}
          <Card title="Goals in this cycle" padded={false}>
            <GoalList goals={current.goals} />
          </Card>
        </>
      )}
    </div>
  );
}
